import { useCallback, useEffect, useState } from 'react';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import { Notice } from '../labour/shared';
import { money } from '../labour/labourUtils';
import insightsService from '../../services/insights';
import fieldWorkService from '../../services/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import type { ActivityType } from '../../types/fieldWork';
import type { Actuals, BudgetLine, BudgetReport, CopyBudgetInput } from '../../types/insights';
import { num, pct, placeKeyOf, placeName, useBudgetPlaces } from './insightsUtils';

const th = 'text-right px-3 py-2 font-medium whitespace-nowrap';
const td = 'px-3 py-2 text-right whitespace-nowrap align-top';

function Used({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const cls = value > 100 ? 'text-red-700' : value > 90 ? 'text-amber-700' : 'text-neutral-500';
  return <span className={`block text-xs ${cls}`}>{pct(value)} used</span>;
}

/**
 * Budget vs actual for a season: one row per budget line, each actual counted
 * against the most specific line that covers it, and what no line covers shown
 * as "Not budgeted". Admins edit the lines here or start them from last season.
 */
export default function BudgetPanel({ season, farmId, canEdit, seasonName }: {
  season: number; farmId: number | null; canEdit: boolean; seasonName: (y: number) => string;
}) {
  const [report, setReport] = useState<BudgetReport | null>(null);
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await insightsService.budgetReport({ season, farm_id: farmId ?? undefined }));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the budget'));
    }
  }, [season, farmId]);
  useEffect(() => { load(); }, [load]);

  if (editing) {
    return <BudgetEditor season={season} seasonName={seasonName} onDone={() => { setEditing(false); load(); }} />;
  }
  if (!report) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;

  const cost = !!report.includes_costs;
  const m = (n: number | null | undefined) => (n == null ? '—' : money(n, report.currency));
  const t = report.totals;

  const actualCells = (a: Actuals, budget?: { hours?: number | null; labour_cost?: number | null; bins?: number | null }) => (
    <>
      <td className={td}>{num(a.hours)}{budget?.hours != null && <span className="block text-xs text-neutral-500">of {num(budget.hours)}</span>}</td>
      {cost && <td className={td}>{m(a.cost)}{budget?.labour_cost != null && <span className="block text-xs text-neutral-500">of {m(budget.labour_cost)}</span>}</td>}
      <td className={td}>{num(a.bins)}{budget?.bins != null && <span className="block text-xs text-neutral-500">of {num(budget.bins)}</span>}</td>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-neutral-500 max-w-2xl">
          Actuals to {report.actuals_to}. Each hour and bin counts once, against the most specific line that covers it.
          The forecast projects cost per bin to the budgeted bins where a line budgets bins.
          {cost && ' Costs ex GST.'}
        </p>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setCopying(true)}><Copy className="h-4 w-4" /> Copy last season</Button>
            <Button size="sm" variant="primary" onClick={() => setEditing(true)}><Pencil className="h-4 w-4" /> Edit budget</Button>
          </div>
        )}
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {!report.exists ? (
        <div className="text-sm text-neutral-500 py-8 text-center space-y-2">
          <p>No budget for {seasonName(season)} yet.</p>
          {canEdit ? <p>Start from last season’s actuals with <strong>Copy last season</strong>, or add lines by hand.</p>
            : <p>A Farm Jobs admin sets the budget.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Place</th>
                <th className="text-left px-3 py-2 font-medium">Work</th>
                <th className={th}>Hours</th>
                {cost && <th className={th}>Cost</th>}
                <th className={th}>Bins</th>
                <th className={th}>Forecast h</th>
                {cost && <><th className={th}>Forecast $</th><th className={th}>Variance</th></>}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {report.rows.map(r => (
                <tr key={r.id}>
                  <td className="px-3 py-2 min-w-[10rem] align-top text-neutral-900">{placeName(r)}</td>
                  <td className="px-3 py-2 whitespace-nowrap align-top">{r.activity_type_name ?? 'All work'}</td>
                  {actualCells(r.actual, r)}
                  <td className={td}>{num(r.forecast.hours)}<Used value={r.hours_used_pct} /></td>
                  {cost && (
                    <>
                      <td className={td}>{m(r.forecast.cost)}<span className="block text-xs text-neutral-500">{r.forecast.method === 'per_bin' ? 'per bin' : ''}</span></td>
                      <td className={`${td} ${r.cost_variance != null && r.cost_variance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {r.cost_variance != null ? `${r.cost_variance > 0 ? '+' : ''}${m(r.cost_variance)}` : '—'}
                        <Used value={r.cost_used_pct} />
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {(report.unbudgeted.hours > 0 || report.unbudgeted.bins > 0) && (
                <tr className="bg-amber-50/50">
                  <td className="px-3 py-2 text-neutral-900 align-top" colSpan={2}>Not budgeted</td>
                  {actualCells(report.unbudgeted)}
                  <td className={td}>{num(report.unbudgeted.hours)}</td>
                  {cost && <><td className={td}>{m(report.unbudgeted.cost)}</td><td className={td}>—</td></>}
                </tr>
              )}
            </tbody>
            <tfoot className="bg-neutral-50 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={2}>Season</td>
                <td className={td}>{num(t.actual.hours)}<span className="block text-xs text-neutral-500">of {num(t.budget.hours)}</span></td>
                {cost && <td className={td}>{m(t.actual.cost)}<span className="block text-xs text-neutral-500">of {m(t.budget.labour_cost)}</span></td>}
                <td className={td}>{num(t.actual.bins)}<span className="block text-xs text-neutral-500">of {num(t.budget.bins)}</span></td>
                <td className={td}>{num(t.forecast.hours)}</td>
                {cost && <><td className={td}>{m(t.forecast.cost)}</td>
                  <td className={td}>{t.forecast.cost != null && t.budget.labour_cost != null ? m(t.forecast.cost - t.budget.labour_cost) : '—'}</td></>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {copying && (
        <CopyModal season={season} seasonName={seasonName} onClose={() => setCopying(false)}
          onDone={() => { setCopying(false); load(); }} />
      )}
    </div>
  );
}

function CopyModal({ season, seasonName, onClose, onDone }: {
  season: number; seasonName: (y: number) => string; onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState<CopyBudgetInput>({ source: 'actuals', uplift_pct: 0, replace: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    try {
      await insightsService.copyBudget(season, form);
      onDone();
    } catch (e) {
      setError(errorMessage(e, 'Could not copy'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormModal title={`Start ${seasonName(season)} from ${seasonName(season - 1)}`} onClose={onClose} onSubmit={submit}
      submitLabel="Copy" submitting={saving} error={error}>
      <Field label="Copy" htmlFor="cb-source">
        <select id="cb-source" className={inputClass} value={form.source}
          onChange={e => setForm({ ...form, source: e.target.value as CopyBudgetInput['source'] })}>
          <option value="actuals">What actually happened last season</option>
          <option value="budget">Last season’s budget</option>
        </select>
      </Field>
      <Field label="Change costs by (%)" htmlFor="cb-uplift" hint="For this year’s rates, e.g. 4. Hours, bins and tonnes copy as they were.">
        <input id="cb-uplift" type="number" inputMode="decimal" className={inputClass} value={form.uplift_pct}
          onChange={e => setForm({ ...form, uplift_pct: Number(e.target.value) || 0 })} />
      </Field>
      <label className="flex items-start gap-2 text-sm text-neutral-700">
        <input type="checkbox" className="mt-1" checked={form.replace} onChange={e => setForm({ ...form, replace: e.target.checked })} />
        <span>Replace lines already in this season’s budget (otherwise only places not yet budgeted are added)</span>
      </label>
    </FormModal>
  );
}

const blank = (): BudgetLine => ({
  farm_id: null, block_id: null, cultivar_area_id: null, activity_type_id: null, hours: null, labour_cost: null, bins: null, tonnes: null,
});

const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

function BudgetEditor({ season, seasonName, onDone }: { season: number; seasonName: (y: number) => string; onDone: () => void }) {
  const places = useBudgetPlaces();
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [lines, setLines] = useState<BudgetLine[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    insightsService.getBudget(season).then(b => setLines(b.lines.length ? b.lines : [blank()]))
      .catch(e => setError(errorMessage(e, 'Could not load the budget')));
    fieldWorkService.getActivityTypes().then(setTypes).catch(() => {});
  }, [season]);

  const set = (i: number, patch: Partial<BudgetLine>) => setLines(ls => ls!.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!lines) return;
    setSaving(true);
    try {
      await insightsService.saveBudget(season, lines);
      onDone();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the budget'));
    } finally {
      setSaving(false);
    }
  };

  if (!lines) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;
  const groups = [...new Set(places.map(p => p.group))];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-neutral-900">Budget for {seasonName(season)}</h3>
        <p className="text-sm text-neutral-500">Costs ex GST. Leave a figure blank to not budget it; a line with no figures is dropped.</p>
      </div>
      <ul className="space-y-3" aria-label="Budget lines">
        {lines.map((l, i) => (
          <li key={i} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
            <label className="col-span-2 text-xs text-neutral-600 min-w-0">
              <span className="block mb-1">Place</span>
              <select className={inputClass} value={placeKeyOf(l)} aria-label={`Place for line ${i + 1}`}
                onChange={e => {
                  const p = places.find(x => x.key === e.target.value);
                  if (p) set(i, { farm_id: p.farm_id, block_id: p.block_id, cultivar_area_id: p.cultivar_area_id });
                }}>
                {!places.some(p => p.key === placeKeyOf(l)) && <option value={placeKeyOf(l)}>{placeName(l)}</option>}
                {groups.map(g => (
                  <optgroup key={g} label={g}>
                    {places.filter(p => p.group === g).map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="col-span-2 sm:col-span-1 text-xs text-neutral-600 min-w-0">
              <span className="block mb-1">Work</span>
              <select className={inputClass} value={l.activity_type_id ?? ''} aria-label={`Work for line ${i + 1}`}
                onChange={e => set(i, { activity_type_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">All work</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-neutral-600 min-w-0">
              <span className="block mb-1">Hours</span>
              <input type="number" inputMode="decimal" min={0} className={inputClass} value={l.hours ?? ''} aria-label={`Hours for line ${i + 1}`}
                onChange={e => set(i, { hours: numOrNull(e.target.value) })} />
            </label>
            <label className="text-xs text-neutral-600 min-w-0">
              <span className="block mb-1">Cost ex GST</span>
              <input type="number" inputMode="decimal" min={0} className={inputClass} value={l.labour_cost ?? ''} aria-label={`Cost for line ${i + 1}`}
                onChange={e => set(i, { labour_cost: numOrNull(e.target.value) })} />
            </label>
            <div className="flex items-end gap-2 min-w-0">
              <label className="text-xs text-neutral-600 flex-1 min-w-0">
                <span className="block mb-1">Bins</span>
                <input type="number" inputMode="decimal" min={0} className={inputClass} value={l.bins ?? ''} aria-label={`Bins for line ${i + 1}`}
                  onChange={e => set(i, { bins: numOrNull(e.target.value) })} />
              </label>
              <button type="button" className="p-2 text-neutral-400 hover:text-red-600" aria-label={`Remove line ${i + 1}`}
                onClick={() => setLines(ls => ls!.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex flex-wrap justify-between gap-2">
        <Button size="sm" variant="outline" onClick={() => setLines(ls => [...ls!, blank()])}><Plus className="h-4 w-4" /> Add line</Button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onDone}>Cancel</Button>
          <Button size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save budget'}</Button>
        </div>
      </div>
    </div>
  );
}
